/* globals EpgEnabled */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { animateScroll } from 'react-scroll';
import SearchComponent from 'components/Search';
import DropdownIcon from 'components/DropdownIcon';
import ReactTimeout from 'react-timeout';
import { helperGetApiRoute, helperGetApiHeaders } from 'helpers/ApiHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { noop } from 'helpers/GlobalHelper';
import { SEARCH_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import {
  shapeFilters,
  OVERLAY_HEADER_DROPDOWN,
  OVERLAY_HEADER_SEARCH,
  OVERLAY_VIDEO_MODAL,
} from 'helpers/FilterHelper';
import {
  cleanString,
  getPreviousPathname,
  helperGetFindUrl,
  helperGetHomeUrl,
  helperGoToRelativeUrl,
} from 'helpers/RouteHelper';
import { getUpNextTimeouts, isLive } from 'helpers/ContentHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import {
  BUILD_VERSION,
  callTealiumEvent,
  Events,
  setDataLayerPageProps,
  getTealiumSearchResultString,
  handleAnalytics,
  PageNames,
  SegmentReferrerProps,
  setPagePropsStorage,
  setTealiumProfileId,
  TEALIUM_EVENT_PREFIX,
  trackPageReferrerAnalytics,
} from '@foxcorp/delta-web-analytics';
import { logEvent } from 'helpers/LogReportHelpers';
import { saveLiveVideo } from 'screens/Live/LiveActions';
import { clearVideo as clearGlobalVideo } from 'components/VideoPlayerGlobal/Actions';
import { fetchPanel, setFirstPage } from 'components/Panel/PanelActions';
import { browserHistory } from 'react-router';
import Dropdown from 'components/Dropdown';
import Style from 'screens/Search/components/SearchDropdown/SearchDropdown.module.scss';
import { doesTabHaveFilter, isDirectLink } from './Helpers';
import SearchResults from './components/SearchResults';
import SearchTabs from './components/SearchTabs';
import {
  reset,
  fetchSearch,
  setFetching,
  fetchNextPage,
  fetchUpNext,
  setTab,
  resetNextPage,
  refreshSearchPanel,
} from './Actions';

import { makeSelectSearchPanels } from './selectors';

const PAGE_TYPE_SEARCH = 'search';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    displayGrid: true,
    autoFocus: true,
    showMobileCancel: false,
    localScrollContainer: true,
    tileAnimation: true,
    preventLazyLoad: false,
  },
  [SITE_PLATFORM_WEB]: {
    displayGrid: true,
    autoFocus: true,
    showMobileCancel: false,
    localScrollContainer: true,
    tileAnimation: true,
    preventLazyLoad: false,
  },
  [SITE_PLATFORM_TABLET]: {
    displayGrid: true,
    autoFocus: false,
    showMobileCancel: true,
    localScrollContainer: false,
    tileAnimation: false,
    preventLazyLoad: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    displayGrid: false,
    autoFocus: false,
    showMobileCancel: true,
    localScrollContainer: false,
    tileAnimation: false,
    preventLazyLoad: true,
  },
};

class Search extends Component {
  constructor(props) {
    super(props);

    let filter = null;
    const {
      panels,
      params: { findCollectionId, findFilterId },
    } = props;
    const preSelectedFilterName = _.toLower(decodeURIComponent(findFilterId));
    if (preSelectedFilterName && panels) {
      const screenPanels = _.get(panels, 'member', []);
      const decodedFindCollectionId = _.toLower(decodeURIComponent(findCollectionId));

      let collectionPanelIndex = _.findIndex(screenPanels, screenPanel => {
        const { id, headline } = screenPanel;
        const isIdMatch = id === findCollectionId;
        const isHeadlineMatch = cleanString(headline) === decodedFindCollectionId;
        return isIdMatch || isHeadlineMatch;
      });
      if (collectionPanelIndex === -1) collectionPanelIndex = 0;

      const panel = screenPanels[collectionPanelIndex];
      const collectionFilters = _.get(panel, 'customViews');
      filter = _.find(
        collectionFilters,
        filter => _.toLower(_.get(filter, 'name')) === preSelectedFilterName,
      );
    }

    this.state = {
      selectedFilter: filter,
      isDropdownOpen: false,
      panelTimeouts: {},
      ignoreHistoryReplace: false,
      upNextTimeouts: [],
      isValidInput: false,
      originalHash: _.get(window, 'location.hash'),
      originalPath: _.get(window, 'location.pathname'),
      previousPathname: getPreviousPathname(),
      newRelicStart: false,
      searchResultCount: 0,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
    };

    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
    this.initialSearchFilterApplied = false;
  }

  componentDidMount() {
    const {
      panels,
      accessToken,
      deviceId,
      fetchSearch,
      pageViewEvent,
      setFetching,
      setOverlay,
    } = this.props;
    const { selectedFilter } = this.state;
    const vh = window.innerHeight * 0.01;
    const panel = _.get(panels, 'member.0');
    let newState = { newRelicStart: true };

    document.documentElement.style.setProperty('--vh', `${vh}px`);
    setOverlay(OVERLAY_HEADER_SEARCH);

    pageViewEvent({ pageName: PageNames.SEARCH });
    handleAnalytics({
      event: Events.FIND_SECTION_SEARCH,
      payload: {
        pageName: 'find:landing',
        pageType: 'find:section-front',
      },
    });
    logEvent('onSearchScreenStart');
    logEvent('onSearchScreenView');
    setDataLayerPageProps();
    handleAnalytics({
      callback: setPagePropsStorage,
      event: Events.SEARCH_STARTED,
      payload: {
        page_name: 'delta:web:find:landing',
        page_type: 'find:section-front',
      },
      tealiumPayload: {
        ...getCommonTealiumProps(),
      },
    });

    if (panel && selectedFilter) {
      this.tabChanged(0, _.get(panel, 'headline'));
      const filters = shapeFilters(_.get(panel, 'customViews'), this.setSelectedFilter);
      const preselectedFilterIndex = _.findIndex(filters, { name: selectedFilter.name });
      newState = { ...newState, selectedFilter: filters[preselectedFilterIndex] };
    }
    if (!panels || !panels.member || panels.member.length === 0) {
      fetchSearch({ accessToken, deviceId });
      setFetching(true);
    }
    this.setState(newState);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { accessToken, deviceId, fetchSearch } = this.props;
    const { accessToken: nextAccessToken } = nextProps;
    const accessTokenChanged = accessToken !== nextAccessToken;
    if (accessTokenChanged) fetchSearch({ accessToken: nextAccessToken, deviceId });
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const {
      selectedTab: currentTab,
      panels: currentPanels,
      params: { findCollectionId, findFilterId },
      location,
      setOverlay,
    } = this.props;
    const { previousPathname } = this.state;
    const {
      selectedTab: nextTab,
      panels: nextPanels,
      params: { findCollectionId: nextFindCollectionId, findFilterId: nextFindFilterId },
      location: nextLocation,
    } = nextProps;
    const { selectedFilter: nextFilter } = nextState;
    const initialDataLoaded = !currentPanels && nextPanels;
    const directLink = isDirectLink();
    const filterUpdated = directLink && nextFindFilterId !== findFilterId;
    const collectionUpdated = directLink && findCollectionId !== nextFindCollectionId;
    const wasDirectLink = _.includes(_.get(location, 'pathname', ''), '/find');
    const isNextDirectLink = _.includes(_.get(nextLocation, 'pathname', ''), '/find');
    const shouldCloseOverlay = wasDirectLink && !isNextDirectLink;
    const shouldUpdateData = initialDataLoaded || collectionUpdated || filterUpdated;

    if (shouldCloseOverlay) setOverlay(OVERLAY_HEADER_SEARCH, false);
    if (currentPanels !== nextPanels) this.fetchLiveTimeouts(nextPanels);

    if (shouldUpdateData) {
      const preSelectedFilterName = _.toLower(decodeURIComponent(nextFindFilterId));
      const screenPanels = _.get(nextPanels, 'member', []);
      const collectionId = collectionUpdated ? nextFindCollectionId : findCollectionId;
      const decodedFindCollectionId = _.toLower(decodeURIComponent(collectionId));

      let collectionPanelIndex = _.findIndex(screenPanels, screenPanel => {
        const { id, headline } = screenPanel;
        const isIdMatch = id === findCollectionId;
        const isHeadlineMatch = cleanString(headline) === decodedFindCollectionId;
        return isIdMatch || isHeadlineMatch;
      });
      if (collectionPanelIndex === -1) collectionPanelIndex = 0;

      const panel = screenPanels[collectionPanelIndex];
      const headline = _.get(panel, 'headline');
      const collectionFilters = _.get(panel, 'customViews');
      const filter = _.find(
        collectionFilters,
        filter => _.toLower(_.get(filter, 'name')) === preSelectedFilterName,
      );
      if (collectionUpdated || filterUpdated) {
        if (previousPathname?.startsWith('/find') || this.initialSearchFilterApplied)
          handleAnalytics({
            event: Events.SEARCH_APPLIED_FILTER,
            payload: filter
              ? { page_filter_title: headline, page_subfilter_title: filter.name }
              : { page_filter_title: headline },
            tealiumPayload: {
              ...getCommonTealiumProps(),
            },
          });
        else this.initialSearchFilterApplied = true;
      }
      this.tabChanged(collectionPanelIndex, headline);
      return this.setState({ selectedFilter: filter });
    }

    if (currentTab === nextTab) return null;

    const filter = { selectedFilter: null };
    const nextFilterName = _.get(nextFilter, 'name');
    if (currentPanels && nextFilter) {
      const filters = _.get(currentPanels, `member[${nextTab}].customViews`, []);
      const matchedFilter = _.find(filters, { name: nextFilterName });
      if (matchedFilter) _.set(filter, 'selectedFilter', matchedFilter);
      return this.setState({ selectedFilter: matchedFilter });
    }
    return this.setState({ selectedFilter: null });
  }

  componentDidUpdate(prevProps) {
    const { panels: prevPanels } = prevProps;
    const { panels } = this.props;
    if (!prevPanels && panels) this.fetchRefreshTimeouts();
  }

  componentWillUnmount() {
    const { reset, setOverlay } = this.props;
    const { ignoreHistoryReplace } = this.state;

    setOverlay(OVERLAY_HEADER_SEARCH, false);
    reset();
    if (isDirectLink() && !ignoreHistoryReplace) {
      const { originalPath, originalHash } = this.state;
      let nextPathHash = `${originalPath}${originalHash}`;
      if (originalPath && _.includes(originalPath, '/find')) nextPathHash = helperGetHomeUrl();
      browserHistory.push(nextPathHash);
    }
  }

  onResultClick = () => {
    const { setOverlay } = this.props;
    animateScroll.scrollTo(0, { duration: 125, smooth: true });
    setOverlay(OVERLAY_HEADER_SEARCH, false);
    setOverlay(OVERLAY_HEADER_DROPDOWN, false);
    setOverlay(OVERLAY_VIDEO_MODAL, false);
  };

  setSearchResultCount = count => this.setState({ searchResultCount: count });

  setSelectedFilter = filter => {
    const {
      selectedFilter,
      resetNextPage,
      params: { findCollectionId },
    } = this.props;
    const { url: nextFilterUrl } = filter;
    const selectedFilterUrl = _.get(selectedFilter, 'url');
    if (selectedFilterUrl !== nextFilterUrl) {
      resetNextPage();
      this.setState({ selectedFilter: filter }, () => {
        const shouldUpdateUrl = isDirectLink();
        if (shouldUpdateUrl) {
          const filterName = encodeURIComponent(_.toLower(_.get(filter, 'name')));
          const nextUrl = helperGetFindUrl(findCollectionId, filterName);
          browserHistory.push(nextUrl);
        }
      });
    }
  };

  closeDropdown = () => {
    this.setState({ isDropdownOpen: false });
  };

  openDropdown = () => {
    this.setState({ isDropdownOpen: true });
    this.scrollToTop();
  };

  scrollToTop = () => {
    animateScroll.scrollTo('searchResultsInfiniteScroll', {
      duration: 1500,
      delay: 0,
      smooth: true,
      containerId: 'searchResultsScrollContainer',
    });
  };

  tabChanged = (idx, headline, collectionType) => {
    const {
      selectedTab,
      panels,
      params: { findFilterId },
      resetNextPage,
      setTab,
    } = this.props;
    const { selectedFilter } = this.state;

    const queryString = _.get(window, 'location.search');
    const campaignCodes = queryString.length > 0 ? queryString : false;

    if (selectedTab !== idx) {
      const selectedFilterIdx = _.get(selectedFilter, 'idx');
      const selectedFilterName =
        _.toLower(_.get(selectedFilter, 'name')) || decodeURIComponent(findFilterId);
      const pathname = _.get(window, 'location.pathname').replace(/\/+$/, '');
      const isFilterAvailableForTab = doesTabHaveFilter(panels, idx, selectedFilterName);
      const nextHeadline = encodeURIComponent(_.toLower(headline));
      const nextPathname = helperGetFindUrl(
        nextHeadline,
        isFilterAvailableForTab && selectedFilterName,
        campaignCodes,
      );
      const shouldUpdateUrl = pathname !== nextPathname && isDirectLink();

      resetNextPage();
      setTab(idx, selectedFilterIdx, headline, collectionType);
      this.closeDropdown();
      if (shouldUpdateUrl) browserHistory.push(nextPathname);
    }
  };

  toggleShowFilter = showFilterStatus => {
    if (showFilterStatus) this.closeDropdown();
  };

  // initiate fetchUpNext timeouts
  fetchLiveTimeouts(panels) {
    const { upNextTimeouts } = this.state;
    const { accessToken, clearTimeout, deviceId, fetchUpNext, setTimeout } = this.props;
    _.forEach(upNextTimeouts, t => clearTimeout(t));
    const member = _.get(panels, 'member');
    if (!member || !member.length) return;
    const newTimeouts = getUpNextTimeouts({
      accessToken,
      deviceId,
      panels: member,
      fetchCb: fetchUpNext,
      setTimeoutCb: setTimeout,
    });
    this.setState({ upNextTimeouts: newTimeouts });
  }

  fetchRefreshTimeouts() {
    const { panels } = this.props;
    if (!panels) return;
    _.forEach(panels.member, panel => this.refreshData({ panel, initialFetch: false }));
  }

  refreshData({ panel, initialFetch }) {
    const { accessToken, clearTimeout, deviceId, refreshSearchPanel, setTimeout } = this.props;
    if (initialFetch) {
      const url = _.get(panel, '@id');
      refreshSearchPanel(accessToken, url, deviceId);
    }
    const { id, validFor } = panel;
    const { panelTimeouts: prevPanelTimeouts } = this.state;
    const newTimeout = {};
    if (prevPanelTimeouts[id]) clearTimeout(prevPanelTimeouts[id]);
    newTimeout[id] = setTimeout(() => {
      this.refreshData({ panel, initialFetch: true });
    }, validFor);
    const panelTimeouts = _.merge(prevPanelTimeouts, newTimeout);
    this.setState({ panelTimeouts });
  }

  renderFilters = panel => {
    const { breakpoint, filteredItems } = this.props;
    const hasFilteredItems = filteredItems && filteredItems.length > 0;
    const customViews = _.get(panel, 'customViews');
    const items = shapeFilters(customViews, this.setSelectedFilter);
    if (!items) return null;
    const panelItems = _.get(panel, 'items.member', null);
    if (!hasFilteredItems && !panelItems) return null;
    const defaultText = _.get(items, '[0].name', null);
    const { selectedFilter, isDropdownOpen } = this.state;
    const selectedFilterName = _.get(selectedFilter, 'name', null);
    const { showMobileCancel } = BREAKPOINTS[breakpoint];

    return (
      <Dropdown
        accessibilityOverride
        items={items}
        defaultText={defaultText}
        getIcon={onClick => <DropdownIcon onClick={onClick} opened={isDropdownOpen} />}
        onOpenDropdown={this.openDropdown}
        onCloseDropdown={this.closeDropdown}
        expanded={isDropdownOpen}
        selected={selectedFilterName}
        theme={Style}
        displayResults={isDropdownOpen}
        showMobileCancel={showMobileCancel}
      />
    );
  };

  renderSearchResults = ({
    searchResults,
    userInput,
    loading,
    resultsTimedOut,
    clearTimer,
  } = {}) => {
    const {
      accessToken,
      deviceId,
      breakpoint,
      clearGlobalVideo,
      panels,
      filteredItems,
      nextPage,
      fetching,
      panelPaginated,
      selectedTab,
      selectedTabName,
      account,
      isAuthenticated,
      preauthorizedResources,
      coordinates,
      saveLiveVideo,
      setCoordinates,
    } = this.props;
    const { newRelicStart } = this.state;
    if (!loading && newRelicStart) {
      logEvent('onSearchScreenFinished');
      this.setState({ newRelicStart: false });
    }
    const { selectedFilter, isValidInput, searchResultCount } = this.state;
    const selectedPanelResultCount = _.get(panels, `member.${selectedTab}.items.totalItems`, null);
    const resultCount = searchResultCount || selectedPanelResultCount;

    const items = selectedFilter ? filteredItems : null;
    const genreFilterName = _.get(
      selectedFilter,
      'name',
      _.get(panels, 'member[0].customViews[0].name', null),
    );
    const { localScrollContainer, displayGrid, tileAnimation, preventLazyLoad } = BREAKPOINTS[
      breakpoint
    ];

    const trackResultSelected = titleText => {
      const { searchCompleted, searchResultCount, trimmedInput } = this.state;
      const payload = {};
      if (userInput) payload.page_search_term = userInput;
      if (resultCount) payload.page_num_search_results = resultCount;
      if (selectedTabName) payload.page_category_title = selectedTabName;
      if (titleText) payload.page_item_title = titleText;

      handleAnalytics({
        event: Events.SEARCH_RESULT_SELECTED,
        payload,
        tealiumPayload: {
          ...getCommonTealiumProps(),
        },
      });

      if (!searchCompleted) {
        handleAnalytics({
          event: Events.SEARCH_COMPLETED,
          payload: {
            page_search_term: trimmedInput,
            page_num_search_results: searchResultCount,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });
      }
    };

    const searchResultsProps = {
      accessToken,
      deviceId,
      loading,
      input: userInput,
      onResultClick: ({ originalItem, titleText }) => {
        trackResultSelected(titleText);

        const itemIsLive = isLive(_.get(originalItem, '@type'));
        if (!EpgEnabled && itemIsLive) {
          clearGlobalVideo({ source: 'HEADER_SEARCH' });
          saveLiveVideo(originalItem);
        }
        let action = 'search_event';
        let profileId = _.get(account, 'profileId');
        let data = setTealiumProfileId(profileId, {
          action,
          platform_version: BUILD_VERSION,
          search_term: userInput,
          search_result_type: 'selected a displayed search result',
          num_search_results: searchResultCount,
          ...getCommonTealiumProps(),
        });
        if (userInput && userInput.length > 1)
          callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
        this.setState({ ignoreHistoryReplace: true }, () => {
          this.onResultClick();
          action = 'search_result_select';
          profileId = _.get(account, 'profileId');
          const searchResultInfo = getTealiumSearchResultString({
            genreFilterName,
            isNation: false,
            itemIsLive,
            originalItem,
            selectedTabName,
            titleText,
          });
          data = setTealiumProfileId(profileId, {
            platform_version: BUILD_VERSION,
            action,
            search_result_info: searchResultInfo,
          });
          if (isValidInput) {
            this.trackPageReferrerAnalytics({
              pageName: PageNames.SEARCH,
              data: {
                [SegmentReferrerProps.NAME]: userInput,
                [SegmentReferrerProps.POSITION]: 'None',
              },
            });
          } else {
            this.trackPageReferrerAnalytics({
              name: PageNames.FIND,
              data: {
                [SegmentReferrerProps.NAME]: selectedTabName,
                [SegmentReferrerProps.POSITION]: genreFilterName,
              },
            });
          }
          callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
        });
      },
      selectedTab,
      results: searchResults,
      filteredItems: items,
      panels,
      panelPaginated,
      fetching,
      hasMore: !!nextPage,
      selectedFilter,
      account,
      userInput,
      isAuthenticated,
      preauthorizedResources,
      coordinates,
      setCoordinates,
      isValidInput,
      localScrollContainer,
      displayGrid,
      tileAnimation,
      preventLazyLoad,
      renderImage: image =>
        renderImage({
          breakpoint,
          imageType: SEARCH_THUMBNAIL_IMAGE,
          ...image,
        }),
      resultsTimedOut,
      setSearchResultCount: this.setSearchResultCount,
      clearTimer,
    };

    return <SearchResults {...searchResultsProps} />;
  };

  renderTabs = () => {
    const { breakpoint, panels, resetNextPage, selectedTab } = this.props;
    const { showMobileCancel } = BREAKPOINTS[breakpoint];
    const { isDropdownOpen, selectedFilter } = this.state;

    return (
      <SearchTabs
        selectedFilter={selectedFilter}
        breakpoint={breakpoint}
        panels={panels}
        fetchSearch={fetchSearch}
        selectedTab={selectedTab}
        setTab={setTab}
        resetNextPage={resetNextPage}
        renderFilters={this.renderFilters}
        tabChanged={this.tabChanged}
        scrollToTop={this.scrollToTop}
        showMobileCancel={showMobileCancel}
        toggleShowFilter={this.toggleShowFilter}
        isDropdownOpen={isDropdownOpen}
      />
    );
  };

  render() {
    const { accessToken, breakpoint, deviceId, seo } = this.props;
    const { isValidInput, previousPathname } = this.state;
    const { search_inputPlaceholder: placeholderText } = window.Language;
    const pageType = 'search';
    const searchHeaders = helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId });
    const searchRoute = helperGetApiRoute(pageType);
    const { autoFocus } = BREAKPOINTS[breakpoint];
    const { localScrollContainer } = BREAKPOINTS[breakpoint];
    const useDoubleQuotes = !!_.get(window.ExternalConfig, 'search.useDoubleQuotes', false);
    const onXClick = () => {
      const isInitialRouteSearch = _.includes(previousPathname, helperGetFindUrl());
      if (isInitialRouteSearch) {
        helperGoToRelativeUrl(helperGetHomeUrl());
      } else {
        helperGoToRelativeUrl(previousPathname);
      }
    };

    // prevent it from getting the result count from last render.
    const getSearchResultCount = () => this.state.searchResultCount;
    const handleSearchComplete = () => this.setState({ searchCompleted: true });
    return (
      <>
        {setHelmetTags(seo)}
        <SearchComponent
          request={{
            headers: searchHeaders,
            route: searchRoute,
            params: '&includeRetired=true&itemsPerPage=25',
          }}
          className="withBackground"
          placeholderText={placeholderText}
          onXClick={onXClick}
          bindXClick={false}
          renderTabs={this.renderTabs}
          renderSearchResults={this.renderSearchResults}
          autofocus={autoFocus}
          handleInputChange={({ input }) => {
            const trimmedInput = input.trim();
            this.setState({
              isValidInput: input.length >= 2 && trimmedInput.length >= 2,
              trimmedInput,
            });
          }}
          renderAltResults
          isValidInput={isValidInput}
          localScrollContainer={localScrollContainer}
          useDoubleQuotes={useDoubleQuotes}
          getSearchResultCount={getSearchResultCount}
          handleSearchCompleted={handleSearchComplete}
        />
      </>
    );
  }
}

function mapStateToProps(state) {
  const panels = _.get(state, 'search.panels', null);

  if (panels) panels.member = panels.member.filter(panel => _.get(panel, 'items', false));

  return {
    panels: makeSelectSearchPanels(state),
    filteredItems: state.search.filteredItems,
    nextPage: state.search.nextPage,
    nextPageSet: state.search.nextPageSet,
    hasMoreItems: state.search.hasMoreItems,
    fetching: state.search.fetching,
    panelPaginated: state.search.panelPaginated,
    selectedTab: state.search.selectedTab,
    selectedTabName: state.search.selectedTabName,
    seo: state.search.SEO,
    suggestedNetwork: state.search.suggestedNetwork,
  };
}

Search.defaultProps = {
  accessToken: null,
  account: {},
  clearGlobalVideo: noop,
  coordinates: {},
  deviceId: null,
  fetching: false,
  filteredItems: [],
  isAuthenticated: null,
  nextPage: null,
  nextPageSet: false,
  panelPaginated: [],
  panels: {},
  params: {},
  preauthorizedResources: [],
  saveLiveVideo: noop,
  selectedFilter: {},
  selectedTab: null,
  selectedTabName: null,
  setCoordinates: noop,
  seo: {},
  suggestedNetwork: null,
};

Search.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  clearGlobalVideo: PropTypes.func,
  clearTimeout: PropTypes.func.isRequired,
  coordinates: PropTypes.shape({}),
  deviceId: PropTypes.string,
  fetchNextPage: PropTypes.func.isRequired,
  fetchSearch: PropTypes.func.isRequired,
  fetchUpNext: PropTypes.func.isRequired,
  fetching: PropTypes.bool,
  filteredItems: PropTypes.arrayOf(PropTypes.shape({})),
  isAuthenticated: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  nextPage: PropTypes.string,
  nextPageSet: PropTypes.bool,
  pageViewEvent: PropTypes.func,
  panelPaginated: PropTypes.arrayOf(PropTypes.shape({})),
  panels: PropTypes.shape({}),
  params: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  refreshSearchPanel: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  resetNextPage: PropTypes.func.isRequired,
  saveLiveVideo: PropTypes.func,
  selectedFilter: PropTypes.shape({}),
  selectedTab: PropTypes.number,
  selectedTabName: PropTypes.string,
  setCoordinates: PropTypes.func,
  setFetching: PropTypes.func.isRequired,
  setOverlay: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
  setTimeout: PropTypes.func.isRequired,
  seo: PropTypes.shape({}),
  suggestedNetwork: PropTypes.string,
};

const TimeoutSearch = ReactTimeout(
  connect(mapStateToProps, {
    clearGlobalVideo,
    fetchPanel,
    fetchSearch,
    refreshSearchPanel,
    fetchNextPage,
    fetchUpNext,
    saveLiveVideo,
    setFetching,
    setTab,
    resetNextPage,
    reset,
    setFirstPage,
  })(Search),
);

Search.fetchData = ({ store, accessToken, params }) =>
  store.dispatch(fetchSearch(accessToken)).then(async response => {
    const { findCollectionId, findFilterId } = params;
    if (!findCollectionId) return Promise.resolve(response);

    const data = _.get(response, 'payload.data', null);
    let panels = _.get(data, `panels.member`, []);
    if (panels) panels = panels.filter(panel => _.get(panel, 'items', false));
    const selectedIndex = panels.findIndex(
      panel => panel.headline.toLowerCase() === findCollectionId,
    );

    const panelData = _.get(panels, selectedIndex, {});
    const headline = _.get(panels, `${selectedIndex}.headline`, null);

    await store.dispatch(setTab(selectedIndex, selectedIndex, headline));

    if (findFilterId) {
      const customViews = _.get(panelData, 'customViews', []);
      const customViewIndex = customViews.findIndex(
        ({ name }) => findFilterId === name.toLowerCase(),
      );
      const { url } = _.get(customViews, customViewIndex, {});
      const headers = helperGetApiHeaders({ pathKey: PAGE_TYPE_SEARCH, accessToken });

      const customViewId = `${url}-${selectedIndex}-${customViewIndex}`;

      await store.dispatch(resetNextPage());
      const result = store.dispatch(fetchPanel(url, headers, customViewId));
      return Promise.resolve(result);
    }

    return Promise.resolve(response);
  });

export default TimeoutSearch;
